import ApiVoip from './apiVoip';

// ============================================================================
// Normalizers — a API serializa TUDO em lowercase (LowercaseContractResolver).
// Convertem lowercase -> camelCase. Aplicar no momento que o dado chega.
// ============================================================================
export const normalizeProvider = (p = {}) => ({
  id: p.id,
  name: p.name,
  displayName: p.displayname ?? p.displayName ?? '',
  logoUrl: p.logourl ?? p.logoUrl ?? null,
  documentationUrl: p.documentationurl ?? p.documentationUrl ?? null,
  callModality: p.callmodality ?? p.callModality ?? '',
  supportedModalities: p.supportedmodalities ?? p.supportedModalities ?? [],
  recommendedModality: p.recommendedmodality ?? p.recommendedModality ?? null,
  capabilities: p.capabilities ?? {},
  configSchema: p.configschema ?? p.configSchema ?? { fields: [] },
  isActive: p.isactive ?? p.isActive ?? true,
  displayOrder: p.displayorder ?? p.displayOrder ?? 0,
});

export const normalizeConfig = (c = {}) => ({
  id: c.id,
  hirerId: c.hirerid ?? c.hirerId,
  voipProviderId: c.voipproviderid ?? c.voipProviderId,
  providerName: c.providername ?? c.providerName ?? null,
  selectedModality: c.selectedmodality ?? c.selectedModality ?? null,
  authMethod: c.authmethod ?? c.authMethod ?? null,
  config: c.config ?? {},
  isActive: c.isactive ?? c.isActive ?? true,
  webhookUrl: c.webhookurl ?? c.webhookUrl ?? null,
});

export const normalizeExtension = (e = {}) => ({
  id: e.id,
  hirerId: e.hirerid ?? e.hirerId,
  voipProviderConfigId: e.voipproviderconfigid ?? e.voipProviderConfigId,
  userId: e.userid ?? e.userId,
  userName: e.username ?? e.userName ?? null,
  extension: e.extension ?? '',
  hasSipPassword: e.hassippassword ?? e.hasSipPassword ?? false,
  isActive: e.isactive ?? e.isActive ?? true,
  providerName: e.providername ?? e.providerName ?? null,
});

export const normalizeTechnician = (t = {}) => ({
  id: t.id,
  name: t.name ?? '',
});

export const normalizeCall = (c = {}) => ({
  id: c.id,
  direction: c.direction ?? '',
  status: c.status ?? '',
  modality: c.modality ?? '',
  fromNumber: c.fromnumber ?? c.fromNumber ?? '',
  toNumber: c.tonumber ?? c.toNumber ?? '',
  calledId: c.calledid ?? c.calledId ?? null,
  assignedUserId: c.assigneduserid ?? c.assignedUserId ?? null,
  startedAt: c.startedat ?? c.startedAt ?? null,
  answeredAt: c.answeredat ?? c.answeredAt ?? null,
  endedAt: c.endedat ?? c.endedAt ?? null,
  durationSeconds: c.durationseconds ?? c.durationSeconds ?? 0,
  recordingStatus: c.recordingstatus ?? c.recordingStatus ?? 'none',
  recordingUrl: c.recordingurl ?? c.recordingUrl ?? null,
  providerCallId: c.providercallid ?? c.providerCallId ?? null,
  matchedUserId: c.matcheduserid ?? c.matchedUserId ?? null,
  matchedOrganizationId: c.matchedorganizationid ?? c.matchedOrganizationId ?? null,
  transcriptionStatus: c.transcriptionstatus ?? c.transcriptionStatus ?? 'none',
  voipProviderConfigId: c.voipproviderconfigid ?? c.voipProviderConfigId ?? null,
  providerName: c.providername ?? c.providerName ?? null,
  outboundCallerId: c.outboundcallerid ?? c.outboundCallerId ?? null,
  metadata: c.metadata ?? null,   // JSON cru do provedor (string)
});

export const normalizeContact = (c = {}) => ({
  id: c.id,
  name: c.name ?? '',
  source: c.source ?? '',          // 'user' | 'customer'
  org: c.org ?? c.organization ?? '',
  phone: c.phone ?? '',
  phoneValid: c.phonevalid ?? c.phoneValid ?? false,
});

export const normalizeSipCredential = (s = {}) => ({
  sipUser: s.sipuser ?? s.sipUser ?? null,
  sipPassword: s.sippassword ?? s.sipPassword ?? null,
  sipDomain: s.sipdomain ?? s.sipDomain ?? null,
  wssUrl: s.wssurl ?? s.wssUrl ?? null,
  iceServers: s.iceservers ?? s.iceServers ?? [],
  ttlSeconds: s.ttlseconds ?? s.ttlSeconds ?? 0,
  modality: s.modality ?? null,
});

// ============================================================================
// Endpoints — retornam o CustomResponse { success, data, errors }.
// ============================================================================

// Catálogo global de plataformas VOIP.
export const getProviders = () => ApiVoip.get('/voip/providers').then(r => r.data);

// Configurações (habilitações) do hirer.
export const getConfigs = () => ApiVoip.get('/voip/provider-configs').then(r => r.data);
export const testConnection = (payload) => ApiVoip.post('/voip/provider-configs/test-connection', payload).then(r => r.data);
export const testCall = (payload, to) => ApiVoip.post(`/voip/provider-configs/test-call?to=${encodeURIComponent(to || '')}`, payload).then(r => r.data);
export const testReceive = (payload) => ApiVoip.post('/voip/provider-configs/test-receive', payload).then(r => r.data);
export const createConfig = (payload) => ApiVoip.post('/voip/provider-configs', payload).then(r => r.data);
export const updateConfig = (id, payload) => ApiVoip.put(`/voip/provider-configs/${id}`, payload).then(r => r.data);
export const deleteConfig = (id) => ApiVoip.delete(`/voip/provider-configs/${id}`).then(r => r.data);
// Gera/regenera o token do webhook (provedores PUSH, ex.: IUNGO) e devolve a URL pronta.
export const regenerateWebhookToken = (id) => ApiVoip.post(`/voip/provider-configs/${id}/webhook-token`, {}).then(r => r.data);

// Técnicos do tenant (dropdown do mapeamento — carregar só ao abrir o modal de adicionar ramal).
export const getTechnicians = () => ApiVoip.get('/voip/agent-extensions/technicians').then(r => r.data);

// Origina uma chamada usando o ramal do agente como caller. calledId opcional vincula ao chamado.
export const callFromExtension = (extensionId, to, calledId) => {
  const cid = calledId ? `&calledId=${encodeURIComponent(calledId)}` : '';
  return ApiVoip.post(`/voip/agent-extensions/${extensionId}/call?to=${encodeURIComponent(to || '')}${cid}`, {}).then(r => r.data);
};

// Busca de contatos (tb_users + tb_2talk_customer, ILIKE 'term%') para discagem.
export const searchContacts = (term) => ApiVoip.get(`/voip/contacts?term=${encodeURIComponent(term || '')}`).then(r => r.data);

// Chamadas: histórico de um chamado (calledId) ou recentes do tenant (sem calledId).
export const getCallsByCalled = (calledId) => ApiVoip.get(`/voip/calls?calledId=${encodeURIComponent(calledId)}`).then(r => r.data);
export const getRecentCalls = (take = 20) => ApiVoip.get(`/voip/calls?take=${take}`).then(r => r.data);
// Força a conciliação do tenant agora (repuxa o history dos provedores poll e atualiza as pendentes).
export const reconcileNow = () => ApiVoip.post('/voip/calls/reconcile', {}).then(r => r.data);
// Associa manualmente uma chamada órfã a um chamado.
export const associateCalled = (callId, calledId) => ApiVoip.post(`/voip/calls/${callId}/associate?calledId=${encodeURIComponent(calledId)}`, {}).then(r => r.data);
// Inbound (pós-chamada): chamados abertos do contato casado (candidatos a vincular).
export const getLinkCandidates = (callId) => ApiVoip.get(`/voip/calls/${callId}/link-candidates`).then(r => r.data);
// Inbound: cria um chamado a partir da ligação recebida e vincula (quick-create).
export const quickCreateCalled = (callId) => ApiVoip.post(`/voip/calls/${callId}/create-called`, {}).then(r => r.data);
// Timeline da chamada (tb_voip_call_event) — modal de detalhes.
export const getCallEvents = (callId) => ApiVoip.get(`/voip/calls/${callId}/events`).then(r => r.data);

// Mapeamento ramal <-> agente.
export const getExtensions = (configId) => ApiVoip.get(`/voip/agent-extensions?configId=${configId}`).then(r => r.data);
export const createExtension = (payload) => ApiVoip.post('/voip/agent-extensions', payload).then(r => r.data);
export const updateExtension = (id, payload) => ApiVoip.put(`/voip/agent-extensions/${id}`, payload).then(r => r.data);
export const deleteExtension = (id) => ApiVoip.delete(`/voip/agent-extensions/${id}`).then(r => r.data);

// Credenciais SIP da sessão do agente logado (discador).
export const getSessionSipCredentials = () => ApiVoip.get('/voip/session/sip-credentials').then(r => r.data);

export default {
  getProviders, getConfigs, testConnection, testCall, testReceive, createConfig, updateConfig, deleteConfig,
  getTechnicians, callFromExtension, getExtensions, createExtension, updateExtension, deleteExtension, getSessionSipCredentials,
  getCallsByCalled, getRecentCalls, associateCalled, getLinkCandidates, quickCreateCalled, getCallEvents, searchContacts,
  regenerateWebhookToken, reconcileNow,
  normalizeProvider, normalizeConfig, normalizeExtension, normalizeTechnician, normalizeSipCredential, normalizeCall, normalizeContact,
};
